/**
 * Every law in one band of the front page's panel.
 *
 * The panel says 26 laws were deeply split. This is the other half of that
 * sentence, because being told a number and not being able to see what is
 * in it is the frustrating way to publish a statistic.
 *
 * Each row leads with the split rather than with the summary. Somebody who
 * arrived by clicking "passed over a party's objection" is here about the
 * vote, so the vote is the thing that is large.
 */

import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, type BandedLaw } from "../api";
import { BAND_BLURB, toneFor } from "../bands";
import { useAsync } from "../useAsync";
import { LawNumber, Problem, Spinner } from "../components/bits";
import { when } from "../format";

const PER_PAGE = 25;

export function Divided() {
  const { band = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") ?? 1);

  const listed = useAsync(() => api.band(band, { page, per_page: PER_PAGE }), [band, page]);
  const tone = toneFor(band);

  if (listed.error) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <Problem error={listed.error} retry={listed.reload} />
        <Link to="/" className="mt-6 inline-block text-sm font-medium text-brand hover:underline">
          ← Back to the front page
        </Link>
      </div>
    );
  }

  // No heading until there is one to write. A placeholder h1 reading
  // "Loading…" is what a screen reader announces the page as, and the band
  // label is the server's to name -- guessing it from the url here would be
  // a second copy of the wording to keep in step.
  if (!listed.data) {
    return (
      <div className="mx-auto max-w-4xl px-5">
        <Spinner label="Gathering the laws…" />
      </div>
    );
  }

  return (
    <>
      <header className={`border-b border-line bg-gradient-to-b ${tone.wash} to-paper`}>
        <div className="mx-auto max-w-4xl px-5 py-12">
          <Link to="/" className="text-sm font-medium text-muted hover:text-ink">
            ← How divided was Congress?
          </Link>
          <h1 className="mt-4 font-display text-4xl font-semibold text-balance">
            <span className={`tnum ${tone.text}`}>{listed.data.total}</span>{" "}
            {listed.data.total === 1 ? "law" : "laws"}:{" "}
            {listed.data.band.label.toLowerCase()}
          </h1>
          <p className="mt-3 max-w-2xl text-pretty text-muted">
            {BAND_BLURB[band] ?? "Laws grouped by how far apart the parties were on final passage."}
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-5 py-10">
        {listed.data && listed.data.laws.length === 0 && (
          <p className="rounded-2xl border border-line bg-card p-8 text-center text-muted">
            No laws fell into this band.
          </p>
        )}

        {listed.data && listed.data.laws.length > 0 && (
          <ul className={`space-y-3 ${listed.loading ? "opacity-60" : ""}`}>
            {listed.data.laws.map((law) => (
              <li key={law.bill_id}>
                <Row law={law} />
              </li>
            ))}
          </ul>
        )}

        {listed.data && listed.data.pages > 1 && (
          <nav className="mt-10 flex items-center justify-center gap-3" aria-label="Pagination">
            <button
              disabled={page <= 1}
              onClick={() => setParams({ page: String(page - 1) }, { replace: true })}
              className="rounded-lg border border-line bg-card px-4 py-2 text-sm font-medium disabled:opacity-40"
            >
              ← Previous
            </button>
            <span className="tnum text-sm text-muted">
              {listed.data.page} / {listed.data.pages}
            </span>
            <button
              disabled={page >= listed.data.pages}
              onClick={() => setParams({ page: String(page + 1) }, { replace: true })}
              className="rounded-lg border border-line bg-card px-4 py-2 text-sm font-medium disabled:opacity-40"
            >
              Next →
            </button>
          </nav>
        )}

        <p className="mt-10 text-sm text-faint">
          A law is filed under whichever chamber it was most divisive in. The House and the Senate
          routinely disagree about that, and reporting the friendlier half would be reporting the
          easier half of its history.
        </p>
      </div>
    </>
  );
}

function Row({ law }: { law: BandedLaw }) {
  return (
    <Link
      to={`/law/${law.bill_id}`}
      className="flex flex-col gap-4 rounded-2xl border border-line bg-card p-5 transition hover:border-ink/25 hover:shadow-[0_2px_20px_-8px_rgba(0,0,0,0.18)] sm:flex-row sm:items-center"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <LawNumber value={law.law_number} />
          <span className="text-xs text-faint capitalize">
            {law.chamber} · {when(law.date)}
          </span>
        </div>
        <p className="mt-1.5 font-display text-lg leading-snug font-semibold text-balance">
          {law.plain_title || law.title}
        </p>
        {law.plain_title && <p className="mt-1 text-sm text-faint">Officially: {law.title}</p>}
      </div>

      <div className="shrink-0 sm:w-56">
        <Split parties={law.parties} />
      </div>
    </Link>
  );
}

/** Share of each party that voted yes. */
function Split({ parties }: { parties: Record<string, number> }) {
  const rows: [string, string, string][] = [
    ["D", "Dem", "bg-dem"],
    ["R", "Rep", "bg-gop"],
  ];
  return (
    <div className="space-y-1.5">
      {rows.map(([key, name, colour]) => {
        // Absent from the payload means that party cast no votes at all,
        // which is not the same as none of them voting for it.
        const share = parties[key];
        return (
          <div key={key} className="flex items-center gap-2">
            <span className="w-7 text-[11px] font-semibold text-faint">{name}</span>
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-sunk">
              {share !== undefined && (
                <span className={`block h-full ${colour}`} style={{ width: `${share * 100}%` }} />
              )}
            </span>
            <span className="tnum w-9 text-right text-[11px] text-muted">
              {share === undefined ? "—" : `${Math.round(share * 100)}%`}
            </span>
          </div>
        );
      })}
      <p className="text-[10px] text-faint">voted for it</p>
    </div>
  );
}
